/**
 * Which page the design editor opens for the current configuration.
 *
 * Moved verbatim out of src/components/product/product-configurator.tsx
 * (selectedSizeValue and the bleed part of editorFinishingOptions) so the
 * storefront and scripts/audit-probo-editor-templates.ts run literally the
 * same resolution. Change it here, for both.
 */
import { sizeLabelDimensionsCm } from '@/lib/products/size-label-dimensions';
import { verifiedWorkfileProfile } from '@/lib/printcom/verified-workfiles';
import { resolveProductionSheetProfile } from '@/lib/editor/production-sheet-selection';
import { supplierBleedLayout } from '@/lib/utils/supplier-bleed';
import { resolveEditorMaterialOverlayUrl } from '@/lib/editor/material-overlay';
import type { ProductOption } from '@/lib/product-options/configurator-rules';

/** Probo products whose editor size already is the ordered size + 2 x 10 mm bleed. */
export const EDITOR_SIZE_INCLUDES_BLEED_SLUGS = ['textielposter', 'wandkleed', 'lichtbak', 'reclamebord', 'gevelbord', 'gevelvlag', 'roll-up-banner'];

export type EditorConfiguration = Record<string, string | number>;

/**
 * Canvas products that keep the online editor although canvas normally goes
 * through the canvas-edge upload flow. Gert, 14 Oct 2026: only the framed
 * canvas (Probo canvas met baklijst), with Probo's 3 cm wrap as bleed.
 */
export const CANVAS_EDITOR_EXCEPTION_SLUGS = ['ingelijst-canvas'];

/** True when this canvas product still opens the editor (see CANVAS_EDITOR_EXCEPTION_SLUGS). */
export function canvasProductAllowsEditor(productSlug: string | null | undefined): boolean {
  return CANVAS_EDITOR_EXCEPTION_SLUGS.includes(String(productSlug || ''));
}

export interface EditorSizeInput {
  productSlug: string;
  options: ProductOption[];
  specifications: Record<string, any> | null | undefined;
  configuration: EditorConfiguration;
}

function positiveNumber(value: unknown): number | null {
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value.replace(',', '.')) : NaN;
  return Number.isFinite(number) && number > 0 ? number : null;
}

/**
 * Final (trim) size in cm when a size value states it next to a larger artwork
 * size. Only used when the metadata says so explicitly; plain widthCm without a
 * bleed/artwork marker keeps the old behaviour.
 */
export function finishedSizeFromMetadata(metadata: any): { width: number; height: number } | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const artworkWidth = positiveNumber(metadata.artworkWidthCm);
  const bleedCm = positiveNumber(metadata.bleedCm);
  const finishedWidth = positiveNumber(metadata.finishedWidthCm);
  const finishedHeight = positiveNumber(metadata.finishedHeightCm);
  const width = positiveNumber(metadata.widthCm);
  const height = positiveNumber(metadata.heightCm);
  const differsFromArtwork = artworkWidth !== null && finishedWidth !== null && Math.abs(artworkWidth - finishedWidth) > 0.01;
  if (finishedWidth !== null && finishedHeight !== null && (differsFromArtwork || bleedCm !== null)) {
    return { width: finishedWidth, height: finishedHeight };
  }
  if (bleedCm !== null && width !== null && height !== null) return { width, height };
  return null;
}

/**
 * Explicit numeric editor safe margin in mm (specifications.safeMarginMm),
 * never parsed from the customer prose in specifications.safeMargin.
 */
export function explicitSafeMarginMm(specifications: Record<string, any> | null | undefined): number | null {
  const value = specifications?.safeMarginMm;
  const number = typeof value === 'number' ? value : typeof value === 'string' && /^\s*\d+(?:[.,]\d+)?\s*$/.test(value) ? Number(value.replace(',', '.')) : NaN;
  return Number.isFinite(number) && number >= 0 ? number : null;
}

type ConfigurationMatch = Record<string, string | number | Array<string | number>>;

function matchesConfiguration(match: unknown, configuration: EditorConfiguration): boolean {
  if (!match || typeof match !== 'object' || Array.isArray(match)) return false;
  const entries = Object.entries(match as ConfigurationMatch);
  if (!entries.length) return false;
  return entries.every(([key, expected]) => {
    const actual = configuration[key];
    if (actual === undefined || actual === null) return false;
    const accepted = Array.isArray(expected) ? expected : [expected];
    return accepted.map(String).includes(String(actual));
  });
}

/**
 * Data-driven switch for products or choices where the online editor cannot
 * make a correct print file (a free-form cut contour, an unverified template).
 * Uploading a ready print file stays possible.
 *
 * specifications.editorAvailability = {
 *   disabledReason?: string,                                    // editor off for the whole product
 *   uploadOnlyWhen?: Array<{ match: Record<optionId, value | value[]>, reason?: string }>,
 * }
 */
export function editorUnavailableReason(specifications: Record<string, any> | null | undefined, configuration: EditorConfiguration): string | null {
  const availability = specifications?.editorAvailability;
  if (!availability || typeof availability !== 'object') return null;
  if (typeof availability.disabledReason === 'string' && availability.disabledReason.trim()) return availability.disabledReason;
  const rules = Array.isArray(availability.uploadOnlyWhen) ? availability.uploadOnlyWhen : [];
  for (const rule of rules) {
    if (rule && typeof rule === 'object' && matchesConfiguration((rule as any).match, configuration)) {
      return typeof (rule as any).reason === 'string' && (rule as any).reason ? (rule as any).reason : 'upload-only';
    }
  }
  return null;
}

export interface EditorContourCut {
  offsetMm: number;
  allowInnerCuts: boolean;
  minFeatureMm: number;
  /** Spot colour name the supplier reads, when the product data names one. */
  separationName?: string;
}

function nonNegativeNumber(value: unknown): number | null {
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value.replace(',', '.')) : NaN;
  return Number.isFinite(number) && number >= 0 ? number : null;
}

/**
 * Contour cut for this configuration: the editor draws the cut line around the
 * design and writes it into the print PDF as a spot colour path.
 *
 * specifications.contourCut = {
 *   when?: Array<{ match: Record<optionId, value | value[]>, offsetMm?, minFeatureMm?, allowInnerCuts? }>,
 *   offsetMm?: number,          // distance design -> cut line, default 3
 *   minFeatureMm?: number,      // warn below this part width, default 5
 *   allowInnerCuts?: boolean,   // cut holes out too, default false
 *   separationName?: string,    // e.g. "Cutcontour" for Probo
 * }
 * Without `when` the whole product is cut on contour.
 */
export function resolveEditorContourCut(specifications: Record<string, any> | null | undefined, configuration: EditorConfiguration): EditorContourCut | null {
  const contour = specifications?.contourCut;
  if (!contour || typeof contour !== 'object' || Array.isArray(contour)) return null;
  const rules = Array.isArray(contour.when) ? contour.when : null;
  const rule = rules ? rules.find((item: any) => item && typeof item === 'object' && matchesConfiguration(item.match, configuration)) : {};
  if (!rule) return null;
  const pick = (key: string, fallback: number) => nonNegativeNumber(rule[key]) ?? nonNegativeNumber(contour[key]) ?? fallback;
  const separationName = typeof contour.separationName === 'string' && /^[A-Za-z][A-Za-z0-9]*$/.test(contour.separationName) ? contour.separationName : undefined;
  return {
    offsetMm: pick('offsetMm', 3),
    minFeatureMm: pick('minFeatureMm', 5),
    allowInnerCuts: Boolean(rule.allowInnerCuts ?? contour.allowInnerCuts),
    ...(separationName ? { separationName } : {}),
  };
}

/** Width from a fixed-width choice (dimensionConstraint.widthByOption) plus a free height. */
function widthByOptionSizeValue(options: ProductOption[], specifications: Record<string, any> | null | undefined, configuration: EditorConfiguration): string | undefined {
  const constraint = specifications?.dimensionConstraint;
  const widthByOption = constraint?.widthByOption;
  if (!widthByOption || typeof widthByOption !== 'object') return undefined;
  const widthOptionId = String(constraint?.widthOptionId || '');
  const heightOptionId = String(constraint?.heightOptionId || 'height_cm');
  if (!widthOptionId || !options.some((option) => option.id === heightOptionId && option.type === 'number')) return undefined;
  const widthCm = positiveNumber(widthByOption[String(configuration[widthOptionId] ?? '')]);
  const heightCm = positiveNumber(configuration[heightOptionId]);
  if (widthCm === null || heightCm === null) return undefined;
  return `${Math.round(widthCm * 10)}_x_${Math.round(heightCm * 10)}_mm`;
}

export function parseSizeDimensionsCm(value: any) {
  // Some supplier size values carry the artwork size (final size + bleed) in
  // value.width/height and the final size in metadata (finishedWidthCm, or
  // widthCm next to bleedCm/artworkWidthCm). The editor works on the final
  // size and adds the bleed itself, so it must not start from the artwork size.
  const finished = finishedSizeFromMetadata(value?.metadata);
  if (finished && value?.metadata?.editorWidthMm === undefined && value?.metadata?.printWidthMm === undefined && value?.metadata?.widthMm === undefined) {
    return finished;
  }
  const widthSource =
    value?.metadata?.editorWidthMm !== undefined ? { value: value.metadata.editorWidthMm, unit: 'mm' } :
    value?.metadata?.printWidthMm !== undefined ? { value: value.metadata.printWidthMm, unit: 'mm' } :
    value?.metadata?.widthMm !== undefined ? { value: value.metadata.widthMm, unit: 'mm' } :
    value?.metadata?.width !== undefined ? { value: value.metadata.width, unit: 'auto' } :
    { value: value?.width, unit: 'auto' };
  const heightSource =
    value?.metadata?.editorHeightMm !== undefined ? { value: value.metadata.editorHeightMm, unit: 'mm' } :
    value?.metadata?.printHeightMm !== undefined ? { value: value.metadata.printHeightMm, unit: 'mm' } :
    value?.metadata?.heightMm !== undefined ? { value: value.metadata.heightMm, unit: 'mm' } :
    value?.metadata?.height !== undefined ? { value: value.metadata.height, unit: 'auto' } :
    { value: value?.height, unit: 'auto' };
  const metadataWidth = Number(widthSource.value);
  const metadataHeight = Number(heightSource.value);
  if (
    Number.isFinite(metadataWidth) &&
    Number.isFinite(metadataHeight) &&
    metadataWidth > 0 &&
    metadataHeight > 0
  ) {
    const divisor = widthSource.unit === 'mm' || heightSource.unit === 'mm'
      ? 10
      : Math.max(metadataWidth, metadataHeight) > 80 ? 10 : 1;
    return { width: metadataWidth / divisor, height: metadataHeight / divisor };
  }

  const text = `${value.label || ''} ${value.value || ''}`.replace(/[″”]/g, '"').replace(/×/g, 'x');
  return sizeLabelDimensionsCm(text);
}

export function getSizeShapeSuffix(value: any) {
  const explicitShape = String(value?.metadata?.printShape || value?.metadata?.shape || '').toLowerCase();
  if (explicitShape === 'circle' || explicitShape === 'round') return '|shape:circle';
  if (explicitShape === 'hexagon') return '|shape:hexagon';
  if (explicitShape === 'heart') return '|shape:heart';
  if (explicitShape === 'star') return '|shape:star';
  if (explicitShape === 'cloud') return '|shape:cloud';

  const text = `${value?.label || ''} ${value?.value || ''} ${value?.metadata?.printcomName || ''}`.toLowerCase();
  if (text.includes('circle') || text.includes('cirkel')) return '|shape:circle';
  if (text.includes('hexagon') || text.includes('zeshoek')) return '|shape:hexagon';
  if (text.includes('heart') || text.includes('hart')) return '|shape:heart';
  if (text.includes('star') || text.includes('ster')) return '|shape:star';
  if (text.includes('cloud') || text.includes('wolk')) return '|shape:cloud';
  return '';
}

export function getConfiguredShapeSuffix(configuration: Record<string, string | number>) {
  const customShape = String(configuration.custom_shape || '').toLowerCase();
  const explicitShape = String(
    customShape.includes('|shape:')
      ? customShape.split('|shape:')[1]
      : (configuration.shape_type || configuration.shape || '')
  ).toLowerCase();
  if (explicitShape === 'round' || explicitShape === 'circle') return '|shape:circle';
  if (explicitShape === 'rounded_corners' || explicitShape === 'rounded-corners') return '|shape:rounded_corners';
  if (explicitShape === 'hexagon') return '|shape:hexagon';
  if (explicitShape === 'heart') return '|shape:heart';
  if (explicitShape === 'star') return '|shape:star';
  if (explicitShape === 'cloud') return '|shape:cloud';
  return '';
}


export function formatSizeForEditor(
  option: ProductOption | undefined,
  rawValue: string | number | undefined,
  configuration: Record<string, string | number>
) {
  if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;

  const raw = String(rawValue);
  if (raw.startsWith('rotate:')) {
    const [, , widthCm, heightCm] = raw.split(':');
    const width = Number(widthCm);
    const height = Number(heightCm);
    if (
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width > 0 &&
      height > 0
    ) {
      return `${Math.round(width * 10)}_x_${Math.round(height * 10)}_mm${getConfiguredShapeSuffix(configuration)}`;
    }
  }

  if (raw.startsWith('custom:')) {
    const [, widthCm, heightCm] = raw.split(':').map(Number);
    if (
      Number.isFinite(widthCm) &&
      Number.isFinite(heightCm) &&
      widthCm > 0 &&
      heightCm > 0
    ) {
      return `${Math.round(widthCm * 10)}_x_${Math.round(heightCm * 10)}_mm${getConfiguredShapeSuffix(configuration)}`;
    }
  }

  const optionWidth = option ? Number(configuration[`${option.id}_width`]) : Number(configuration.size_width);
  const optionHeight = option ? Number(configuration[`${option.id}_height`]) : Number(configuration.size_height);
  if (
    (raw === 'custom' || option?.values?.some((value) => value.customSizes && String(value.value) === raw)) &&
    Number.isFinite(optionWidth) &&
    Number.isFinite(optionHeight) &&
    optionWidth > 0 &&
    optionHeight > 0
  ) {
    return `${Math.round(optionWidth * 10)}_x_${Math.round(optionHeight * 10)}_mm${getConfiguredShapeSuffix(configuration)}`;
  }

  const selectedValue = option?.values?.find((value) =>
    String(value.value) === raw || String(value.id) === raw
  );
  const dimensions = selectedValue ? parseSizeDimensionsCm(selectedValue) : null;
  if (dimensions) {
    return `${Math.round(dimensions.width * 10)}_x_${Math.round(dimensions.height * 10)}_mm${getSizeShapeSuffix(selectedValue) || getConfiguredShapeSuffix(configuration)}`;
  }

  const paperSizeText = `${selectedValue?.label || ''} ${selectedValue?.value || raw}`;
  const paperSizeMatch = paperSizeText.match(/\b(A[0-6])\b/i);
  if (paperSizeMatch) {
    const paperSizes: Record<string, { width: number; height: number }> = {
      A0: { width: 841, height: 1189 },
      A1: { width: 594, height: 841 },
      A2: { width: 420, height: 594 },
      A3: { width: 297, height: 420 },
      A4: { width: 210, height: 297 },
      A5: { width: 148, height: 210 },
      A6: { width: 105, height: 148 },
    };
    const size = paperSizes[paperSizeMatch[1].toUpperCase()];
    if (size) return `${size.width}_x_${size.height}_mm${getConfiguredShapeSuffix(configuration)}`;
  }

  return `${raw}${getConfiguredShapeSuffix(configuration)}`;
}


/** The editor size value (trim size, "W_x_H_mm" plus an optional shape suffix) for a configuration. */
export function resolveEditorSizeValue({ productSlug, options, specifications, configuration }: EditorSizeInput): string | undefined {
  const official = verifiedWorkfileProfile(productSlug, configuration)?.pages[0];
  if (official) return `${official.widthMm}_x_${official.heightMm}_mm`;
  // A supplier work sheet (werktekening) is the exact page; the upload's file
  // check should compare against it too.
  let sheetPage: { widthMm: number; heightMm: number } | undefined;
  try {
    sheetPage = resolveProductionSheetProfile(specifications || undefined, configuration)?.pages[0];
  } catch {
    sheetPage = undefined;
  }
  if (sheetPage) return `${sheetPage.widthMm}_x_${sheetPage.heightMm}_mm`;
  if (EDITOR_SIZE_INCLUDES_BLEED_SLUGS.includes(productSlug) && configuration.width_cm && configuration.height_cm) {
    const widthCm = Number(configuration.width_cm);
    const heightCm = Number(configuration.height_cm);

    if (Number.isFinite(widthCm) && Number.isFinite(heightCm)) {
      return `${widthCm + 2}_x_${heightCm + 2}_cm`;
    }
  }

  if (productSlug === 'inductie-beschermer' && configuration.width_cm && configuration.height_cm) {
    const widthCm = Number(configuration.width_cm);
    const heightCm = Number(configuration.height_cm);

    if (Number.isFinite(widthCm) && Number.isFinite(heightCm)) {
      return `${widthCm}_x_${heightCm}_cm`;
    }
  }

  const dimensionConstraint = specifications?.dimensionConstraint;
  const widthOptionId = String(dimensionConstraint?.widthOptionId || 'width_cm');
  const heightOptionId = String(dimensionConstraint?.heightOptionId || 'height_cm');
  const hasFreeDimensionInputs =
    options.some((option) => option.id === widthOptionId && option.type === 'number') &&
    options.some((option) => option.id === heightOptionId && option.type === 'number');
  const widthCm = Number(configuration[widthOptionId]);
  const heightCm = Number(configuration[heightOptionId]);
  if (
    hasFreeDimensionInputs &&
    Number.isFinite(widthCm) &&
    Number.isFinite(heightCm) &&
    widthCm > 0 &&
    heightCm > 0
  ) {
    return `${Math.round(widthCm * 10)}_x_${Math.round(heightCm * 10)}_mm${getConfiguredShapeSuffix(configuration)}`;
  }

  const editorSizes = specifications?.editorSizes;
  if (editorSizes && typeof editorSizes === 'object') {
    const variantKey = String(configuration.variantSku || configuration.prodigiSku || configuration.sku || '');
    // Printful-synchronisatie sleutelt editorSizes op de maatwaarde
    // ("Circular ⌀3 in": "76_x_76_mm"). Zonder die stap las de editor de
    // optiebreedte 76 als centimeters (760 mm).
    const sizeKey = configuration.size !== undefined ? String(configuration.size) : '';
    const editorSize = (variantKey ? editorSizes[variantKey] : undefined) ?? (sizeKey ? editorSizes[sizeKey] : undefined);
    if (typeof editorSize === 'string' && editorSize) return editorSize;
    if (editorSize && typeof editorSize === 'object') {
      const orientation = String(configuration.orientation || 'portrait');
      const orientedSize = editorSize[orientation] || editorSize.square || editorSize.portrait || editorSize.landscape;
      if (typeof orientedSize === 'string' && orientedSize) return orientedSize;
    }
    if (variantKey.startsWith('rotate:')) {
      const sourceSku = variantKey.replace(/^rotate:/, '');
      const rotatedEditorSize = editorSizes[sourceSku];
      if (rotatedEditorSize && typeof rotatedEditorSize === 'object') {
        const orientedSize = rotatedEditorSize.landscape || rotatedEditorSize.portrait || rotatedEditorSize.square;
        if (typeof orientedSize === 'string' && orientedSize) return orientedSize;
      }
    }
  }

  if (typeof specifications?.editorSizeValue === 'string' && specifications.editorSizeValue) {
    return specifications.editorSizeValue;
  }

  const fixedWidthSize = widthByOptionSizeValue(options, specifications, configuration);
  if (fixedWidthSize) return fixedWidthSize;

  const preferredKeys = [
    'size',
    'format',
    'dimensions',
    'UnifiedPaperFormat',
    'PaperFormat',
    'CanvasFormat',
    'UnifiedCanvasFormat',
  ];
  for (const key of preferredKeys) {
    if (configuration[key]) {
      const option = options.find((item) => item.id === key || item.name === key);
      return formatSizeForEditor(option, configuration[key], configuration);
    }
  }

  const sizeLikeOption = options.find((option) => {
    const text = `${option.id} ${option.name || ''} ${option.label || ''}`.toLowerCase();
    return text.includes('size') || text.includes('format') || text.includes('formaat') || text.includes('dimension');
  });

  return sizeLikeOption && configuration[sizeLikeOption.id]
    ? formatSizeForEditor(sizeLikeOption, configuration[sizeLikeOption.id], configuration)
    : undefined;
}

/** Bleed and safe-margin hints the editor reads from finishingOptions (__bleedMm, __bleedOutside, __safeMarginMm). */
export function resolveEditorBleedFinishing({ productSlug, specifications, configuration }: Omit<EditorSizeInput, 'options'>): Record<string, string | number> {
  const next: Record<string, string | number> = {};
  if (verifiedWorkfileProfile(productSlug, configuration)) next.__bleedMm = 0;
  const specificationBleed = specifications?.bleed ?? specifications?.fileRequirements?.bleed;
  const specificationSafeMargin = specifications?.fileRequirements?.safeMargin ?? specifications?.safeMargin;
  if (specificationBleed !== undefined && specificationBleed !== null && specificationBleed !== '') {
    next.__bleedMm = typeof specificationBleed === 'number' ? specificationBleed : String(specificationBleed);
  }
  // Supplier wants the bleed on top of the ordered size (Print.com: aan te
  // leveren formaat = eindformaat + 2 x afloop). The editor then makes the page
  // that much larger and puts the TrimBox on the ordered size.
  // These slugs already pass ordered size + 2 cm as the editor size (see selectedSizeValue).
  if (!verifiedWorkfileProfile(productSlug, configuration) && !(EDITOR_SIZE_INCLUDES_BLEED_SLUGS.includes(productSlug) && configuration.width_cm && configuration.height_cm)
    && supplierBleedLayout(specifications)?.outside) next.__bleedOutside = 1;
  if (specificationSafeMargin !== undefined && specificationSafeMargin !== null && specificationSafeMargin !== '') {
    const numericSafeMargin = Number(specificationSafeMargin);
    if (Number.isFinite(numericSafeMargin)) next.__safeMarginMm = numericSafeMargin;
  }
  // A numeric safeMarginMm wins: safeMargin is often customer prose.
  const explicitSafeMargin = explicitSafeMarginMm(specifications);
  if (explicitSafeMargin !== null) next.__safeMarginMm = explicitSafeMargin;
  if (productSlug === 'doming-stickers' && configuration.custom_shape === 'rectangular') {
    next.rounded_corners = '1.5_mm_radius';
  }
  // Canvas wrap: depth of the frame side in mm. The bleed beyond it folds to the back.
  const wrapSideMm = nonNegativeNumber(specifications?.canvasWrap?.sideMm);
  if (wrapSideMm) next.__wrapSideMm = wrapSideMm;
  const contour = resolveEditorContourCut(specifications, configuration);
  if (contour) {
    next.__contourCut = 1;
    next.__contourOffsetMm = contour.offsetMm;
    next.__contourMinFeatureMm = contour.minFeatureMm;
    next.__contourInnerCuts = contour.allowInnerCuts ? 1 : 0;
    if (contour.separationName) next.__cutContourName = contour.separationName;
  } else if (typeof specifications?.cutContourName === 'string' && /^[A-Za-z][A-Za-z0-9]*$/.test(specifications.cutContourName)) {
    // Supplier spot colour name for the circle cut of round stickers.
    next.__cutContourName = specifications.cutContourName;
  }
  // Material preview (e.g. wood planks) drawn over the design; never printed.
  const materialOverlayUrl = resolveEditorMaterialOverlayUrl(specifications, configuration);
  if (materialOverlayUrl) next.__materialOverlayUrl = materialOverlayUrl;
  return next;
}
